import React, { useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  IconButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import MultiSelect from "pages/TargetForm/MultiSelect";
import MultSelectNew from "./BaselineEmission/MultSelectNew";
import { useMenuWidth } from "hooks/useMenuWidth";
import { getSelectMenuProps, SELECT_COMPONENT_SX } from "constants/selectStyles";


interface FilterPanelProps {
  locationOptions: { id: number; label: string; value: string }[];
  selectedLocations: number[];
  onLocationChange: (selectedOptions: number[]) => void;
  showBaseYear?: boolean;
  baseYear?: number;
  baseYearOptions?: number[];
  onBaseYearChange?: (value: number) => void;
  showScope?: boolean;
  scopeOptions?: { id: number; label: string }[];
  selectedScope?: number[];
  onScopeChange?: (selectedOptions: number[]) => void;
  onSearch: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  locationOptions,
  selectedLocations,
  onLocationChange,
  showBaseYear = false,
  baseYear,
  baseYearOptions = [],
  onBaseYearChange,
  showScope = false,
  scopeOptions = [],
  selectedScope = [],
  onScopeChange,
  onSearch,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const toggleFilters = () => setShowFilters(!showFilters);
  const { menuWidth, handleMenuOpen } = useMenuWidth();

  return (
    <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%' }}>
      <Paper
        variant="outlined"
        sx={{
          padding: "16px",
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        {/* Toggle Button */}
        <button
          type="button"
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
            width: "100%",
          }}
          data-testid="toggleFiltersButon"
          onClick={toggleFilters}
          aria-pressed={showFilters}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: "14.4px",
              color: "#27272E",
              marginLeft: "4px",
            }}
            data-testid="filtersLabel"
          >
            Filters
          </Typography>
          <div style={{ marginLeft: "auto" }}>
            <IconButton>{showFilters ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
          </div>
        </button>

        {/* Filters Section */}
        {showFilters && (
          <>
            {/* Locations Filter */}
            <Grid container alignItems="flex-start">
              <Grid item xs={6} sx={{ mr: 5 }}>
                <Typography variant="body1" sx={{ mr: 5, fontSize: "12px", mb: 1 }}>
                  Locations
                </Typography>
                <MultSelectNew
                  dataTestId="locationSelect"
                  options={locationOptions}
                  onSelectionChange={onLocationChange}
                  selectedOptions={selectedLocations}
                />
              </Grid>

              {/* Base Year Filter (Optional) */}
              {showBaseYear && onBaseYearChange && (
                <Grid item xs={4}>
                  <FormControl fullWidth sx={{ fontSize: 12, minWidth: 0, ml: 0 }}>
                    <FormLabel sx={{ mb: 1, fontSize: "12px" }}>Base Year</FormLabel>
                    <Select
                      value={baseYear}
                      onChange={(e) => onBaseYearChange?.(Number(e.target.value))}
                      data-testid="baseYearDrodpdown"
                      MenuProps={getSelectMenuProps(menuWidth)}
                      sx={SELECT_COMPONENT_SX}
                      onOpen={handleMenuOpen}
                    >
                      <MenuItem value={0} sx={{ fontSize: 12 }}>Select Start Year</MenuItem>
                      {baseYearOptions.map((option, index) => (
                        <MenuItem key={option} value={option} sx={{ fontSize: 12 }}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>

            {/* Scope Filter (Optional) */}
            {showScope && onScopeChange && (
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  <Typography variant="body1" sx={{ fontSize: "12px", m: 1 }}>
                    Scope
                  </Typography>
                  <MultiSelect
                    dataTestId={"scopeSelect"}
                    options={scopeOptions ?? []}
                    onSelectionChange={onScopeChange}
                    selectedOptions={selectedScope}
                  />
                </Grid>
              </Grid>
            )}

            {/* Search Button */}
            <Grid container justifyContent="flex-end">
              <Grid item sx={{ ml: 2 }}>
                <Button type="submit" variant="contained" onClick={onSearch} data-testid="btnSearch">
                  Search
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Grid>
  );
};

export default FilterPanel;
